import React, {useState, useEffect, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';

import UploadDocumentContainer from 'components/UploadDocument/UploadDocument.container';
import {getMinorDetailsMeta, uploadMinorDetails} from 'api';
import {handleApiError, triggerToast} from 'helpers';
import {LoaderContainer, Spinner} from '../commons';

import {DateToDdMmYyyy, DateToIso, getAgeFromDOB, isFutureDob} from 'utils/dateUtils';

import {MinorDetailsFormFields} from './components';
import {getMinorDetailsFormSchema} from './MinorDetailsSchema';
import IMAGES from 'constants/Images';

const propTypes = {
  updateStatus: PropTypes.func.isRequired,
  kycData: PropTypes.object.isRequired,
};

const AADHAAR_CAUTION = 'Aadhaar card is not accepted (as per regulations)';

const MinorDetails = ({updateStatus, kycData}) => {
  const [minorFormData, setMinorFormData] = useState({name: '', dob: '', gender: ''});
  const [minorMeta, setMinorMeta] = useState(null);
  const [isFetchingMeta, setIsFetchingMeta] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [formKey, setFormKey] = useState(0);

  const schema = useMemo(
    () => getMinorDetailsFormSchema(minorMeta?.allowedGenders),
    [minorMeta?.allowedGenders]
  );

  const handleMinorFormChange = useCallback((change) => {
    setMinorFormData((prev) => {
      return {
        ...prev,
        [change.field]: change.value,
      };
    });
  }, []);

  const applyPrefill = useCallback((metaData) => {
    const fromMetaName = metaData?.prefill?.name?.trim();
    const fromMetaDob = metaData?.prefill?.dob?.trim();
    const fromMetaGender = (metaData?.prefill?.gender || '').trim();
    const nextName = fromMetaName || '';
    const rawDob = fromMetaDob || '';
    setMinorFormData({
      name: nextName,
      dob: DateToIso(rawDob),
      gender: fromMetaGender,
    });
    setFormKey((k) => k + 1);
  }, []);

  const fetchMeta = useCallback(async () => {
    setIsFetchingMeta(true);
    try {
      const res = await getMinorDetailsMeta();
      setMinorMeta(res.data);
      applyPrefill(res.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsFetchingMeta(false);
    }
  }, [applyPrefill]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  const handleSubmitFile = async (formData) => {
    const dob = formData.get('dob');
    const formattedDob = DateToDdMmYyyy(dob);

    if (isFutureDob(dob) || getAgeFromDOB(dob) >= 18) {
      triggerToast('Minor must be under 18 years of age', 'error');
      return;
    }

    if (formattedDob) {
      formData.set('dob', formattedDob);
    }

    setErrorMessage(false);
    setIsUploading(true);
    try {
      const res = await uploadMinorDetails(formData);
      if (res.status === 200) {
        updateStatus();
      } else if (res.status === 400) {
        res.json().then((data) => {
          setErrorMessage(data.detail);
        });
      } else if (!res.ok) {
        throw {
          response: {
            status: 400,
            data: {
              detail: {
                message: 'File Format Not Supported',
              },
            },
          },
        };
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsUploading(false);
    }
  };

  const extrasFormAppender = (formData, selectedOption) => {
    formData.append('name', (minorFormData.name || '').trim());
    formData.append('dob', minorFormData.dob);
    formData.append('gender', (minorFormData.gender || '').trim());
    formData.append('doc_type', selectedOption?.key);
  };

  const documentOptions = (minorMeta?.allowedDocTypes || []).map((opt) => {
    return {
      ...opt,
      id: opt.key,
    };
  });

  const fieldsFilled = Boolean(
    (minorFormData.name || '').trim() &&
      (minorFormData.dob || '').trim() &&
      (minorFormData.gender || '').trim()
  );

  const prependContent = (
    <>
      <MinorDetailsFormFields
        formKey={formKey}
        schema={schema}
        data={minorFormData}
        onChange={handleMinorFormChange}
      />
      <div
        role="alert"
        style={{
          background: '#fff3cd',
          border: '1px solid #ffeeba',
          color: '#c38042',
          borderRadius: '6px',
          padding: '10px 12px',
          marginBottom: '12px',
          fontSize: '0.9rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <img src={IMAGES.caution_icon} width="16" alt="aadhaar-not-accepted" />
        <span>{AADHAAR_CAUTION}</span>
      </div>
    </>
  );

  return isFetchingMeta ? (
    <LoaderContainer>
      <Spinner color="blue" message="Loading minor details, please wait" />
    </LoaderContainer>
  ) : (
    <>
      {minorMeta ? (
        <UploadDocumentContainer
          isLoading={isUploading}
          onSubmitDocument={handleSubmitFile}
          subHeading="<span class='heading-text'>Enter details of the Minor</span>"
          heading="Details of the Minor"
          documentOptions={documentOptions}
          errorMessage={errorMessage}
          prependContent={prependContent}
          extrasFormAppender={extrasFormAppender}
          isBtnDisabled={!fieldsFilled}
        />
      ) : null}
    </>
  );
};

MinorDetails.propTypes = propTypes;

export default MinorDetails;
